import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface ScheduleFormData {
  name: string;
  phone: string;
  email: string;
  platform: string;
}

interface ContactContextType {
  showContactModal: boolean;
  showScheduleModal: boolean;
  isSubmitting: boolean;
  submitSuccess: boolean;
  submitError: string | null;
  openContactModal: () => void;
  closeContactModal: () => void;
  openScheduleModal: () => void;
  closeScheduleModal: () => void;
  submitContactForm: (data: ContactFormData) => Promise<boolean>;
  submitScheduleForm: (data: ScheduleFormData) => Promise<boolean>;
  scrollToContact: () => void;
  resetSubmitStatus: () => void;
}

const ContactContext = createContext<ContactContextType | undefined>(undefined);

export const useContact = (): ContactContextType => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error('useContact must be used within a ContactProvider');
  }
  return context;
};

interface ContactProviderProps {
  children: ReactNode;
}

export const ContactProvider: React.FC<ContactProviderProps> = ({ children }) => {
  const [showContactModal, setShowContactModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const openContactModal = useCallback(() => {
    setShowContactModal(true);
    setShowScheduleModal(false);
  }, []);

  const closeContactModal = useCallback(() => {
    setShowContactModal(false);
  }, []);

  const openScheduleModal = useCallback(() => {
    setShowScheduleModal(true);
    setShowContactModal(false);
  }, []);

  const closeScheduleModal = useCallback(() => {
    setShowScheduleModal(false);
  }, []);

  const resetSubmitStatus = useCallback(() => {
    setSubmitSuccess(false);
    setSubmitError(null);
  }, []);

  const scrollToContact = useCallback(() => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const submitContactForm = useCallback(async (data: ContactFormData): Promise<boolean> => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const response = await fetch('https://formspree.io/f/mqavbdab', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          _subject: data.subject || `New Contact from ${data.name}`,
          name: data.name,
          email: data.email,
          message: data.message,
          form_type: 'contact'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setSubmitSuccess(true);
      return true;
    } catch (error) {
      setSubmitError('Failed to send message. Please try again.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const submitScheduleForm = useCallback(async (data: ScheduleFormData): Promise<boolean> => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const response = await fetch('https://formspree.io/f/mqavbdab', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          _subject: `Meeting Request - ${data.name}`,
          name: data.name,
          email: data.email,
          phone: data.phone,
          platform: data.platform,
          message: `Meeting request via ${data.platform}`,
          form_type: 'schedule'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send meeting request');
      }

      setSubmitSuccess(true);
      setShowScheduleModal(false);
      return true;
    } catch (error) {
      setSubmitError('Failed to submit meeting request. Please try again.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const value: ContactContextType = {
    showContactModal,
    showScheduleModal,
    isSubmitting,
    submitSuccess,
    submitError,
    openContactModal,
    closeContactModal,
    openScheduleModal,
    closeScheduleModal,
    submitContactForm,
    submitScheduleForm,
    scrollToContact,
    resetSubmitStatus,
  };

  return (
    <ContactContext.Provider value={value}>
      {children}
    </ContactContext.Provider>
  );
};

export default ContactContext;
